import { Button, Checkbox, DatePicker, Form, Input, Modal, Select, Table, TimePicker, message } from "antd";
import { useEffect, useMemo, useState } from "react";
import { Calendar } from "../../../models/calendar/Calendar.model";
import { Absence, mapToAbsence } from "../../../models/calendar/Absence.model";
import { AbsenceService } from "../../../services/absence.api.client";
import { Contact, ContactService } from "../../../services/contact.api.client";
import { useCompany } from "../../../contexts/CompanyContext";
import { useConfig } from "../../../contexts/ConfigContext";
import dayjs from "../../../utils/dayjs";

type Props = { calendar: Calendar };

const ConfigurationAbsences: React.FC<Props> = ({ calendar }) => {
  const { company } = useCompany();
  const { config } = useConfig();
  const [form] = Form.useForm();
  const allDay = Form.useWatch("allDay", form);
  const [rows, setRows] = useState<Absence[]>([]);
  const [loading, setLoading] = useState(true);
  const [employees, setEmployees] = useState<Contact[]>([]);
  const operators = useMemo(
    () =>
      employees
        .map((c) => ({ value: Number(c.id), label: c.name || c.uid || String(c.id) }))
        .filter((o) => Number.isFinite(o.value) && o.value > 0),
    [employees]
  );

  const reload = () => {
    if (!company.current.uid || !calendar.uid) return;
    setLoading(true);
    AbsenceService.getAbsences(company.current.uid, calendar.uid)
      .then((res) => {
        const list = res.absenceList || res || [];
        setRows(Array.isArray(list) ? list.map(mapToAbsence) : []);
      })
      .catch((err) => message.error(err.message || "No se pudieron cargar las ausencias"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    reload();
  }, [company.current.uid, calendar.uid]);

  useEffect(() => {
    if (!company.current.uid || !config.key) return;
    ContactService.getBulkContacts("EMPLOYEE", company.current.uid, config.key)
      .then((res) => {
        const list = res?.contactList || [];
        setEmployees(Array.isArray(list) ? list : []);
      })
      .catch((err) => message.error(err.message || "No se pudieron cargar los empleados"));
  }, [company.current.uid, config.key]);

  const showConflicts = (conflicts: any[]) => {
    if (!conflicts?.length) return;
    Modal.warning({
      title: `Hay ${conflicts.length} cita(s) en esta ausencia`,
      content: (
        <ul style={{ paddingLeft: 16, margin: 0 }}>
          {conflicts.map((c) => (
            <li key={c.id || c.uid}>
              {c.title || c.summary || "Cita"} — {c.start ? dayjs(c.start).tz().format("DD/MM HH:mm") : ""}
            </li>
          ))}
        </ul>
      ),
      okText: "Entendido",
    });
  };

  const onFinish = async (values: any) => {
    if (!config.key) {
      message.error("No hay clave de seguridad");
      return;
    }
    const range = values.range;
    const payload: Record<string, unknown> = {
      company: company.current.uid,
      company_id: company.current.id,
      uidCalendar: calendar.uid,
      contact_id: values.contact_id,
      dateFrom: range[0].format("YYYY-MM-DD"),
      dateTo: range[1].format("YYYY-MM-DD"),
      all_day: Boolean(values.allDay),
      note: values.note || null,
      created_by_id: undefined,
    };
    if (!values.allDay) {
      payload.timeStart = values.timeStart.format("HH:mm");
      payload.timeEnd = values.timeEnd.format("HH:mm");
    }
    try {
      const res = await AbsenceService.createAbsence(payload, config.key);
      showConflicts(res.conflicts || []);
      form.resetFields();
      form.setFieldsValue({ allDay: true });
      reload();
      message.success("Ausencia guardada");
    } catch (err: any) {
      message.error(err.message || "No se pudo guardar");
    }
  };

  const onDelete = (uid: string) => {
    if (!config.key) return;
    AbsenceService.deleteAbsence({ uid, company: company.current.uid }, config.key)
      .then(() => {
        message.success("Ausencia eliminada");
        reload();
      })
      .catch((err) => message.error(err.message || "No se pudo eliminar"));
  };

  return (
    <div>
      <Form form={form} layout="vertical" onFinish={onFinish} initialValues={{ allDay: true }}>
        <Form.Item name="contact_id" label="Operario" rules={[{ required: true, message: "Elige un operario" }]}>
          <Select
            options={operators}
            placeholder="Empleado"
            showSearch
            optionFilterProp="label"
            notFoundContent={operators.length === 0 ? "No hay empleados" : undefined}
          />
        </Form.Item>
        <Form.Item name="range" label="Fechas" rules={[{ required: true, message: "Elige fechas" }]}>
          <DatePicker.RangePicker style={{ width: "100%" }} />
        </Form.Item>
        <Form.Item name="allDay" valuePropName="checked">
          <Checkbox>Todo el día</Checkbox>
        </Form.Item>
        {!allDay && (
          <>
            <Form.Item name="timeStart" label="Desde" rules={[{ required: true, message: "Hora inicio" }]}>
              <TimePicker format="HH:mm" style={{ width: "100%" }} />
            </Form.Item>
            <Form.Item name="timeEnd" label="Hasta" rules={[{ required: true, message: "Hora fin" }]}>
              <TimePicker format="HH:mm" style={{ width: "100%" }} />
            </Form.Item>
          </>
        )}
        <Form.Item name="note" label="Nota">
          <Input.TextArea rows={2} />
        </Form.Item>
        <Button type="primary" htmlType="submit">
          Guardar ausencia
        </Button>
      </Form>
      <Table
        style={{ marginTop: 16 }}
        size="small"
        loading={loading}
        rowKey="uid"
        dataSource={rows}
        pagination={false}
        columns={[
          { title: "Operario", render: (_, r) => r.contact?.name || r.contact_id },
          {
            title: "Cuando",
            render: (_, r) => {
              const start = dayjs(r.start_at).tz();
              const end = dayjs(r.end_at).tz();
              if (r.all_day) {
                const last = end.subtract(1, "minute");
                return `${start.format("DD/MM")} – ${last.format("DD/MM")} (todo el día)`;
              }
              return `${start.format("DD/MM HH:mm")} – ${end.format("HH:mm")}`;
            },
          },
          { title: "Nota", dataIndex: "note", ellipsis: true },
          {
            title: "",
            render: (_, r) => (
              <Button type="link" danger onClick={() => onDelete(r.uid)}>
                Borrar
              </Button>
            ),
          },
        ]}
      />
    </div>
  );
};

export default ConfigurationAbsences;
